import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, Shield, ArrowLeft, Edit, Check } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import companyCollaboratorDetailViewModel from '../../view-model/professional/company/company-collaborator-detail-view-model';

interface CompanyCollaboratorViewProps {
  onBack: () => void;
  onEdit?: () => void;
}

const CompanyCollaboratorDetailView: React.FC<CompanyCollaboratorViewProps> = observer(({
  onBack,
  onEdit
}) => {
  const { t } = useTranslation();
  const vm = companyCollaboratorDetailViewModel;

  // Charger les données au montage du composant
  useEffect(() => {
    vm.loadCollaboratorData();
  }, [vm]);

  // Afficher un loader pendant le chargement
  if (vm.isLoading || !vm.data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-gray-400">
          {t('common.loading', 'Chargement...')}
        </div>
      </div>
    );
  }

  // Afficher une erreur si le chargement a échoué
  if (vm.error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">{vm.error}</div>
      </div>
    );
  }

  const { collaborator, role, permissions } = vm.data;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-semibold">
            {t('collaborator.view.title', 'Détails du collaborateur')}
          </h2>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            <Edit size={16} />
            <span>{t('collaborator.view.edit', 'Modifier')}</span>
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* En-tête avec photo et nom */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              {collaborator.picture ? (
                <img
                  src={collaborator.picture}
                  alt={vm.getFormattedName(collaborator.firstName, collaborator.lastName)}
                  className="h-20 w-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700">
                  <span className="text-xl font-semibold text-gray-500 dark:text-gray-400">
                    {vm.getInitials(collaborator.firstName, collaborator.lastName)}
                  </span>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {vm.getFormattedName(collaborator.firstName, collaborator.lastName)}
              </h3>
              <div className="flex items-center mt-1">
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${vm.getRoleBadgeClass(role.name)}`}>
                  {role.name}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informations du collaborateur */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('collaborator.view.contactInfo', 'Informations de contact')}
            </h4>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="text-gray-400 flex-shrink-0" size={18} />
                <div className="text-gray-700 dark:text-gray-300">
                  {vm.getFormattedName(collaborator.firstName, collaborator.lastName)}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="text-gray-400 flex-shrink-0" size={18} />
                <div className="text-gray-700 dark:text-gray-300">{collaborator.email}</div>
              </div>

              {collaborator.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="text-gray-400 flex-shrink-0" size={18} />
                  <div className="text-gray-700 dark:text-gray-300">{collaborator.phone}</div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('collaborator.view.roleDetails', 'Rôle et permissions')}
            </h4>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Shield className="text-gray-400" size={18} />
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {role.name}
                </h5>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {role.description}
              </p>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
            {t('collaborator.view.permissions', 'Permissions')}
            <span className="ml-2 text-sm font-normal text-gray-500">({permissions.length})</span>
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-2">
            {Object.entries(vm.groupedPermissions).map(([action, resources]) => (
                <div key={action} className="bg-gray-50 dark:bg-gray-750 p-2 rounded-lg">
                  <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-1 text-sm">
                    {action} <span className="text-xs font-normal text-gray-500">({resources.length})</span>
                  </h5>
                  <ul className="space-y-0.5 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {resources.map((resource, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Check className="h-3 w-3 text-green-500 mr-1.5 flex-shrink-0" />
                        <span>{resource}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default CompanyCollaboratorDetailView;
